<template>
    <LoginForm
        v-if="showLogin"
        :pre-filled-email="validatedEmail"
        class="form"
        @login="handleLoginSuccess"
    >
        <template #title>
            <div class="login-title">
                It looks like you already have an account. Please log in (or
                <ThButton
                    class="th-button"
                    type="link"
                    @click="resetEmail"
                    >use another email
                </ThButton>
                ).
            </div>
        </template>
    </LoginForm>

    <EnquiryFormForm
        v-else
        :disabled="fetchingNewCustomerSubmit || fetchingCustomerExists"
        :errors="errors"
        class="form"
        @submit="submitNewCustomer"
    >
        <template #title>
            {{ signUpStep.title }}
        </template>

        <template
            v-if="signUpStep.description"
            #description
        >
            {{ signUpStep.description }}
        </template>
        <template
            v-if="signUpStep.submitButtonLabel"
            #submit
        >
            {{ signUpStep.submitButtonLabel }}
        </template>
    </EnquiryFormForm>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

import { useCustomerExistsLoginForm } from '@/composables/app/useCustomerExistsLoginForm'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import type { QuestionnaireSignUpStepFragment } from '@/graphql/api'
import type { ErrorCollectionWithTraces } from '@/helpers/errors'

import EnquiryFormForm from '@/components/enquiry/EnquiryFormForm.vue'
import LoginForm from '@/components/user/LoginForm.vue'

defineProps<{
    fetchingNewCustomerSubmit: boolean
    errors: ErrorCollectionWithTraces
    signUpStep: QuestionnaireSignUpStepFragment
}>()

const emits = defineEmits<{
    'login-success': []
    'submit-new-customer': []
}>()

const { validatedEmail, customerRegistrationInput } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const {
    fetchingCustomerExists,
    showLogin,
    closeLoginForm,
    resetEmail,
    getCustomerExists,
} = useCustomerExistsLoginForm(validatedEmail)

const handleLoginSuccess = () => {
    closeLoginForm()

    emits('login-success')
}

const submitNewCustomer = async () => {
    const customer = customerRegistrationInput.value

    if (!customer) {
        return
    }

    const exists = await getCustomerExists()

    if (exists) {
        return
    }

    emits('submit-new-customer')
}
</script>

<style lang="scss" scoped>
.form {
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    left: 0;
    padding: 96px 0;
    min-height: calc(100vh - 100px);

    @include min-width(1024) {
        width: 180%;
    }
}
</style>
